// WEEK: who you're overdue to call and why now. Radar scope on the left, flight strips on the right.
import type { PrivateNote } from "../../shared/network";
import { radar, type RadarRow } from "../../shared/engine";
import type { MountFn } from "../context";
import { h, mount, fmtDay } from "../ui/dom";
import { strip, type StripLine } from "../ui/strip";
import { createScope, type Blip } from "../ui/scope";
import { emptyState, key, seg } from "../ui/widgets";

const PAGE = 40;
const isWhyNow = (r: RadarRow) => r.why.some((w) => !/^(No contact in|Close relationship)/.test(w));

type SortKey = "score" | "days" | "str" | "name" | "company";
const SORTS: { value: SortKey; label: string }[] = [
  { value: "score", label: "PRIORITY" },
  { value: "days", label: "DAYS" },
  { value: "str", label: "STRENGTH" },
  { value: "name", label: "NAME" },
  { value: "company", label: "COMPANY" },
];
// Text sorts start A-Z; number sorts start highest first. Clicking the active key flips it.
const firstDir = (k: SortKey): 1 | -1 => (k === "name" || k === "company" ? 1 : -1);
const lastName = (n: string) => n.split(" ").slice(-1)[0].toLowerCase() + n.toLowerCase();

export const mountWeek: MountFn = (el, ctx) => {
  if (!ctx.network) {
    mount(el, h("div", { class: "pagehead" }, h("h1", { class: "title" }, "This week")), emptyState(() => ctx.navigate("sources")));
    return;
  }
  let shown = PAGE;
  let selected: string | null = null;
  const noteOpen = new Set<string>();
  const drafts = new Map<string, string>();
  const snaps = new Map<string, RadarRow>(); // rows Peter marked this session stay on screen so he can undo
  let rows: RadarRow[] = [];
  let sortBy: SortKey = "score";
  let dir: 1 | -1 = -1;

  const scope = createScope((k) => select(k, true));
  const counts = h("div", { class: "counts" });
  const sortBar = h("div", { class: "weeksort" });
  const list = h("div", { class: "strips" });
  const more = h("div", { class: "stripmore" });
  const legend = h(
    "div",
    { class: "legend" },
    h("span", null, h("i", { style: "background:var(--amber)" }), "Why now"),
    h("span", null, h("i", { style: "background:var(--red)" }), "Personal"),
    h("span", null, h("i", { style: "background:var(--green)" }), "Overdue"),
    h("span", null, h("i", { style: "background:var(--cyan)" }), "Marked"),
  );
  const head = h("div", { class: "pagehead" }, h("h1", { class: "title" }, "This week"), h("span", { class: "meta" }, "AS OF ", h("b", { class: "num" }, ctx.asOf)), h("span", { class: "grow" }));
  mount(
    el,
    head,
    h("div", { class: "week" }, h("div", { class: "scopewrap" }, scope.el, legend), h("div", null, counts, sortBar, list, more)),
  );

  function compute() {
    const fresh = radar(ctx.network!, { asOf: ctx.asOf, notes: ctx.state.notes, contacted: ctx.state.contacted });
    // A row Peter marked this session keeps its place (so he can undo) until he leaves the page.
    for (const k of [...snaps.keys()]) if (!ctx.state.contacted[k]) snaps.delete(k);
    const have = new Set(fresh.map((r) => r.c.k));
    const merged = fresh.map((r) => snaps.get(r.c.k) ?? r);
    for (const [k, r] of snaps) if (!have.has(k)) merged.push(r);
    rows = sortRows(merged);
  }

  function sortRows(rs: RadarRow[]): RadarRow[] {
    // A marked row counts as contacted today, the same as its strip shows.
    const days = (r: RadarRow) => (ctx.state.contacted[r.c.k] ? 0 : r.s.daysSince ?? 99999);
    const val: Record<SortKey, (r: RadarRow) => number | string> = {
      score: (r) => r.score,
      days,
      str: (r) => r.c.rs,
      name: (r) => lastName(r.c.n),
      company: (r) => r.c.co.toLowerCase(),
    };
    const v = val[sortBy];
    // Ties fall back to priority so the order stays stable and useful.
    return rs.sort((a, b) => {
      const x = v(a), y = v(b);
      return (x < y ? -1 : x > y ? 1 : 0) * dir || b.score - a.score;
    });
  }

  function setSort(k: SortKey) {
    if (k === sortBy) dir = (dir * -1) as 1 | -1;
    else (sortBy = k), (dir = firstDir(k));
    rows = sortRows(rows);
    render();
  }

  const toneOf = (r: RadarRow): Blip["tone"] => (ctx.state.contacted[r.c.k] ? "cyan" : r.personal ? "red" : isWhyNow(r) ? "amber" : "green");

  function toggleHold(k: string) {
    const n: PrivateNote = ctx.state.notes[k] ?? { note: "", hold: false, noPitch: false };
    const next = { ...n, hold: !n.hold };
    void ctx.setNote(k, !next.note && !next.hold && !next.noPitch && !next.sectors?.length ? null : next);
  }

  function noteEditor(k: string): HTMLElement {
    const cur = ctx.state.notes[k];
    const ta = h("textarea", { class: "mine", rows: 3, placeholder: "Private. Only you see this.", "aria-label": "Private note" });
    ta.value = drafts.get(k) ?? cur?.note ?? "";
    ta.addEventListener("input", () => drafts.set(k, ta.value));
    setTimeout(() => ta.focus(), 0);
    const close = () => (noteOpen.delete(k), drafts.delete(k), render());
    const save = () => {
      const text = ta.value.trim();
      const base: PrivateNote = cur ?? { note: "", hold: false, noPitch: false };
      const next = { ...base, note: text };
      void ctx.setNote(k, !text && !next.hold && !next.noPitch && !next.sectors?.length ? null : next);
      close();
    };
    return h("div", { class: "strip-extra" }, h("div", { class: "micro cy" }, "PRIVATE NOTE"), ta, h("div", { class: "keys" }, key("SAVE", save, { primary: true, sm: true }), key("CANCEL", close, { sm: true })));
  }

  function stripFor(r: RadarRow): HTMLElement {
    const k = r.c.k;
    const marked = !!ctx.state.contacted[k];
    const note = ctx.state.notes[k];
    const lines: StripLine[] = [];
    if (r.personal) {
      lines.push({ tone: "red", text: r.hold?.kind === "private" ? "Personal check-in only. " + r.hold.why : r.why[0] });
    } else {
      for (const w of r.why) lines.push({ tone: /^(No contact in|Close relationship)/.test(w) ? "green" : "amber", text: w });
      const t = r.s.lastTouch;
      if (t) {
        const q = h("span", { class: "q" }, `'${t[3]}'`);
        lines.push({ tone: "dim", text: "", html: h("span", null, `${fmtDay(t[0])} · ${t[1].toUpperCase()} · ${t[2] === "I" ? "IN" : "OUT"} · `, q) });
      }
    }
    if (note?.note) lines.push({ tone: "cyan", text: "NOTE  " + note.note.slice(0, 160) });
    const bar = marked ? "cyan" : r.personal ? "red" : isWhyNow(r) ? "amber" : "green";
    const hold = !!note?.hold;
    return strip({
      c: r.c,
      daysSince: marked ? 0 : r.s.daysSince,
      bar,
      lines,
      selected: selected === k,
      onSelect: () => select(k, false),
      extra: noteOpen.has(k) ? noteEditor(k) : null,
      keys: [
        key("CONTACTED", () => {
          if (marked) void ctx.markContacted(k, null);
          else {
            snaps.set(k, r);
            void ctx.markContacted(k, ctx.asOf);
          }
        }, { on: marked, sm: true }),
        h("div", { class: "keys" }, [
          Object.assign(key("HOLD", () => toggleHold(k), { sm: true, cls: "hold" + (hold ? " on" : "") }), {}),
          key("NOTE", () => (noteOpen.has(k) ? noteOpen.delete(k) : noteOpen.add(k), render()), { on: noteOpen.has(k) || !!note?.note, sm: true }),
        ]),
      ],
    });
  }

  function render() {
    const nWhy = rows.filter(isWhyNow).length;
    const nPers = rows.filter((r) => r.personal).length;
    mount(
      counts,
      h("div", null, h("b", { class: "num" }, String(rows.length)), "OVERDUE"),
      h("div", { class: "am" }, h("b", { class: "num" }, String(nWhy)), "WHY-NOW"),
      h("div", { class: "rd" }, h("b", { class: "num" }, String(nPers)), "PERSONAL"),
      h("div", null, `1-${Math.min(shown, rows.length)} OF ${rows.length}`),
    );
    mount(
      sortBar,
      h("span", { class: "micro" }, "SORT"),
      seg(SORTS.map((o) => ({ value: o.value, label: o.label + (o.value === sortBy ? (dir === 1 ? " ▲" : " ▼") : "")})), sortBy, setSort),
    );
    mount(list, rows.slice(0, shown).map(stripFor));
    mount(more, rows.length > shown ? key(`SHOW ${Math.min(PAGE, rows.length - shown)} MORE`, () => ((shown += PAGE), render())) : null);
    if (!rows.length) mount(list, h("div", { class: "empty" }, h("div", { class: "t" }, "NOTHING OVERDUE AS OF " + ctx.asOf)));
    scope.update(
      rows.map((r) => ({ k: r.c.k, name: r.c.n, days: ctx.state.contacted[r.c.k] ? 0 : (r.s.daysSince ?? 800 + (r.c.k.length * 97 + r.c.k.charCodeAt(r.c.k.length - 1) * 31) % 1700), strength: r.c.rs, tone: toneOf(r), type: r.c.ty })),
    );
    scope.select(selected);
  }

  function select(k: string, fromScope: boolean) {
    selected = selected === k && !fromScope ? null : k;
    const i = rows.findIndex((r) => r.c.k === k);
    if (i >= shown) shown = i + 12;
    render();
    if (selected && fromScope) list.querySelector(`[data-k="${CSS.escape(k)}"]`)?.scrollIntoView({ block: "center" });
  }

  compute();
  render();
  const off = ctx.onChange(() => {
    compute();
    render();
  });
  return () => {
    off();
    scope.destroy();
  };
};
